/**
 * StepThree - Adventure Builder Final Step
 * 
 * Final step of the adventure builder where users can preview and test
 * their adventure form exactly as clients will see it. Now uses the
 * InteractiveAdventurePreview component for better reusability.
 * 
 * @author Wildlife Project Team
 * @version 2.2 - Refactored to use InteractiveAdventurePreview component
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { type OptionType } from '../types';
import { InteractiveAdventurePreview } from '../components/InteractiveAdventurePreview';

interface StepThreeProps {
  adventureName: string;
  adventureDescription: string;
  basePrice: number;
  basePriceType: 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
  currency: string;
  options: OptionType[];
  isSubmitting: boolean;
  onBack: () => void;
  onFinish: () => void;
}

export function StepThree({
  adventureName,
  adventureDescription,
  basePrice,
  basePriceType,
  currency,
  options,
  isSubmitting,
  onBack,
  onFinish
}: StepThreeProps) {
  return (
    <div className="w-full flex flex-col items-center">
      {/* Interactive Adventure Preview */}
      <InteractiveAdventurePreview
        adventureName={adventureName}
        adventureDescription={adventureDescription}
        basePrice={basePrice}
        basePriceType={basePriceType}
        currency={currency}
        options={options}
        showHeader={true}
      />

      {/* Action Button */}
      <div className="w-full max-w-2xl mb-6">
        <Button
          onClick={onFinish}
          disabled={isSubmitting}
          className="w-full bg-primary hover:bg-[#1e4a20] text-white py-3 text-lg font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Creating Adventure...' : 'Create Adventure'}
        </Button>
      </div>

      {/* Navigation */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={isSubmitting}
          className="text-gray-600 hover:text-gray-800 transition-colors flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Options
        </Button>
      </div>
    </div>
  );
} 